import React from 'react';

import { LoaderLine } from '@/components/LoaderLine';
import { PaymentSystemButton } from '@/components/PaymentSystemButton';
import { StripeCardForm } from '@/components/StripeCardForm';
import { PAYMENT_SECTION_ID } from '@/constants';
import { PAYWALL_NOTES } from '@/constants/campaign-notes';
import { ONE_OFF_PLAN_IDS } from '@/constants/plans';
import { usePaywallTermsCheckbox } from '@/hooks/usePaywallTermsCheckbox';
import { useStripeV2Payment } from '@/hooks/useStripeV2Payment';
import { useStripeV2Plans } from '@/hooks/useStripeV2Plans';
import { CombinedPaymentDescription } from '@/modals/CoinsPaywall/components/CombinedPaymentDescription';
import { webCampaign } from '@/modules/webCampaign';
import { StripeElements } from '@/pages/Paywall/components/StripeElements';
import { StripeV2PaymentRequest } from '@/services/stripe';
import { canUseApplePay } from '@/utils/canUseApplePay';

import { OneOffPaymentDescription } from './components/OneOffPaymentDescription/OneOffPaymentDescription';
import { Option, Options } from './components/Options';
import { PaymentDescription } from './components/PaymentDescription';
import { ApplePayLabel, CreditCardLabel } from './components/PaySysLabel';
import {
  PaymentOptionsWrapper,
  SectionStyles,
  TitleStyles,
} from './PaymentMethodSelection.styles';

export const SectionPaymentMethodSelection: React.FC<{
  theme?: 'domination' | 'dark-forest';
}> = ({ theme }) => {
  const { stripePlans, selectedPlan, selectedCoinPlan, billingPortalLink } =
    useStripeV2Plans();

  const requireTermsCheckbox = webCampaign.setup.paywallNotes.includes(
    PAYWALL_NOTES.REQUIRE_TERMS_CHECKBOX,
  );
  const { isChecked: isTermsChecked } = usePaywallTermsCheckbox();
  const isPaymentDisabled = requireTermsCheckbox && !isTermsChecked;

  // Determine if this is a one-off payment
  const isOneOffPlan = selectedPlan?.id
    ? selectedPlan.id.startsWith('pro.one_off_') ||
      ONE_OFF_PLAN_IDS.includes(
        selectedPlan.id as (typeof ONE_OFF_PLAN_IDS)[number],
      )
    : false;

  const [paymentTabs, setPaymentTabs] = React.useState<
    ('applePay' | 'creditCard' | 'googlePay')[]
  >(['creditCard']);

  const [paymentMethod, setPaymentMethod] = React.useState<
    'applePay' | 'creditCard' | 'googlePay'
  >(() => {
    return 'creditCard';
  });

  React.useEffect(() => {
    if (canUseApplePay()) {
      setPaymentTabs(['applePay', 'creditCard']);
      setPaymentMethod('applePay');
    }
  }, []);

  const {
    mutateAsync: createPayment,
    isLoading: isCreatingStripeSubscription,
  } = useStripeV2Payment();

  const [clientSecret, setClientSecret] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchClientSecret = async () => {
      if (selectedPlan?.id) {
        const excludeBank =
          webCampaign.setup.paywallNotes?.includes(
            PAYWALL_NOTES.EXCLUDED_PAYMENT_METHODS_BANK,
          ) ?? false;

        const paymentData: StripeV2PaymentRequest = {
          subscription_price_id: selectedPlan.id,
          ...(excludeBank && { excluded_payment_methods: ['bank'] as const }),
        };

        if (selectedCoinPlan) {
          paymentData.coins_price_id = selectedCoinPlan.id;
        }

        const result = await createPayment(paymentData);
        setClientSecret(result.secret);
      }
    };
    fetchClientSecret();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan, selectedCoinPlan]);

  const renderDescription = () => {
    if (!selectedPlan) return null;

    if (selectedCoinPlan) {
      return (
        <CombinedPaymentDescription
          selectedPlan={selectedPlan}
          selectedCoinPlan={selectedCoinPlan}
        />
      );
    }

    if (isOneOffPlan) {
      return <OneOffPaymentDescription selectedPlan={selectedPlan} />;
    }
    return <PaymentDescription selectedPlan={selectedPlan} />;
  };

  return (
    <SectionStyles id={PAYMENT_SECTION_ID}>
      <TitleStyles>Payment method</TitleStyles>

      {isCreatingStripeSubscription || !clientSecret || !stripePlans ? (
        <LoaderLine label="Loading payment elements..." />
      ) : (
        <StripeElements
          clientSecret={clientSecret}
          key={clientSecret}
          stripeTheme="flat"
        >
          {selectedPlan && (
            <PaymentOptionsWrapper $disabled={isPaymentDisabled}>
              <Options>
                {paymentTabs.includes('applePay') && (
                  <Option
                    selected={paymentMethod === 'applePay'}
                    onClick={() => setPaymentMethod('applePay')}
                    label="Apple Pay"
                    labelIcon={<ApplePayLabel />}
                  >
                    {renderDescription()}
                    <PaymentSystemButton
                      plan={selectedPlan}
                      disabled={
                        isCreatingStripeSubscription || isPaymentDisabled
                      }
                      billing_portal_link={billingPortalLink || ''}
                      clientSecret={clientSecret}
                      selectedCoinPlan={selectedCoinPlan}
                    />
                  </Option>
                )}
                <Option
                  selected={paymentMethod === 'creditCard'}
                  onClick={() => setPaymentMethod('creditCard')}
                  label="Credit card"
                  labelIcon={<CreditCardLabel />}
                  theme={theme}
                >
                  {renderDescription()}
                  <StripeCardForm
                    clientSecret={clientSecret}
                    billing_portal_link={billingPortalLink || ''}
                    plan={selectedPlan}
                    disabled={isCreatingStripeSubscription || isPaymentDisabled}
                    theme={theme}
                    buttonText={isOneOffPlan ? 'Buy Now' : 'Pay'}
                    selectedCoinPlan={selectedCoinPlan}
                  />
                </Option>
              </Options>
            </PaymentOptionsWrapper>
          )}
        </StripeElements>
      )}
    </SectionStyles>
  );
};
